/* eslint-disable @typescript-eslint/no-explicit-any */
import { apiServices } from "@/services/api.services";
import { formatErrorDetails, setFormError } from "@/services/error.services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { setToken } from "@/lib/token";
import { useNavigate } from "react-router-dom";
import { GLOBAL_MESSAGES } from "@/lib/global-message";
import { DEFAULT_ROUTE } from "@/lib/static";
import { decodeToken, encodeTexts } from "./toolkit/utils.lib";
import { users } from "@/services/global.services";
import { useUserInfo } from "@/global-data/use-user-info";

type CreateUserPayload = {
  username: string;
  password: string;
};
export const useLogin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const { setUser } = useUserInfo();

  const formSchema = z.object({
    username: z.string().min(1, {
      message: "Username is required",
    }),
    password: z.string().min(1, {
      message: "Password is required",
    }),
  });

  type LoginFormValues = z.infer<typeof formSchema>;

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const loadUser = async ({
    company_id,
    user_id,
    token,
  }: {
    company_id: string;
    user_id: string;
    token: string;
  }) => {
    const payload = {
      data: {
        user_id: user_id,
        filter_by: {
          company_id: [company_id],
          search_text: "",
          exact_match: false,
        },
      },
      company_id: company_id,
      user_id: user_id,
    };
    const { data, error, message } = await apiServices<any>(
      users.get,
      "post",
      payload,
      {},
      token
    );

    if (error) {
      toast({
        title: "Error",
        variant: "destructive",
        description: error.status?.message || message,
      });
    }
    return { data, error };
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const { error } = await login(values);
    setFormError(error, form.setError);
  }

  const login = async (formPayload: CreateUserPayload) => {
    const username = encodeTexts(formPayload.username);
    const password = encodeTexts(formPayload.password);
    const payload: any = new FormData();

    payload.append("username", username);
    payload.append("password", password);

    const { data, message, error } = await apiServices<CreateUserPayload>(
      "/login",
      "post",
      payload
    );

    if (error) {
      const formatedError = formatErrorDetails(error);
      toast({
        title: "Login Failed",
        description: error?.status?.msg || message,
        variant: "destructive",
      });
      return { error: formatedError };
    }

    const token = data?.access_token;

    if (!token) {
      return {};
    }
    const user: any = decodeToken(token);
    if (!user) {
      return {};
    }

    const userInfoResponse = await loadUser({
      company_id: user.company_id,
      user_id: user.user_id,
      token: token,
    });

    const currentUser = userInfoResponse?.data?.result?.metadata?.users?.[0];

    if (currentUser) {
      setUser(currentUser);
      // storeUser(currentUser);

      setToken(token);
      toast({
        description: GLOBAL_MESSAGES.LOGIN_DATA.SUCCESS,
        variant: "success",
      });
      navigate(DEFAULT_ROUTE);
    }

    return {};
  };

  return { login, onSubmit, form };
};
